// src/modules/gerenciador/ProfessoresDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  collection,
  writeBatch,
  query,
  where,
  getDocs
} from "firebase/firestore";
import { useAuth, logAuditoria } from '../../context/AuthContext';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { limparCPF } from '../../utils/helpers';

// --- CONSTANTES ---
const POSTOS_PERMITIDOS = [
  "Cel PM", "Ten Cel PM", "Maj PM", "Cap PM", "1º Ten PM", "2º Ten PM", "Asp OF PM"
];

// --- ÍCONES ---
const EditIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>;
const TrashIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const PlusIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>;

const normalizeText = (text) => {
  if (!text) return "";
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
};

export const ProfessoresDashboard = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Dados
  const [professores, setProfessores] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [materias, setMaterias] = useState([]);

  // Estados de Interface
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentItem, setCurrentItem] = useState(null);
  
  const [aulaTemp, setAulaTemp] = useState({ curso_id: '', materia_id: '' });
  const [postoSelection, setPostoSelection] = useState(""); 

  // Estados para Filtro e Abas
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('todos');

  // Estado para controlar o menu dropdown
  const [actionMenuOpenId, setActionMenuOpenId] = useState(null);

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    
    const unsubProfessores = onSnapshot(collection(db, paths.professores), (snap) => {
        const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setProfessores(lista.sort((a, b) => (a.nome_completo || '').localeCompare(b.nome_completo || '')));
    });

    const unsubCursos = onSnapshot(collection(db, paths.cursos), (snap) => {
        const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setCursos(lista.sort((a,b) => (a.nome || '').localeCompare(b.nome || '')));
    });

    const unsubMaterias = onSnapshot(collection(db, paths.materias), (snap) => {
        const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMaterias(lista.sort((a,b) => (a.nome || '').localeCompare(b.nome || '')));
    });
    
    setLoading(false);
    return () => { unsubProfessores(); unsubCursos(); unsubMaterias(); };
  }, []);

  // Fecha o menu se clicar fora
  useEffect(() => {
      const handleClickOutside = (event) => {
          if (!event.target.closest('.action-menu-container')) {
              setActionMenuOpenId(null);
          }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Toggle do Menu Dropdown
  const toggleActionMenu = (id, e) => {
      e.stopPropagation();
      setActionMenuOpenId(actionMenuOpenId === id ? null : id);
  };

  // --- LÓGICA DE FILTRAGEM ---
  const filteredProfessores = useMemo(() => {
      return professores.filter(p => {
          const searchClean = normalizeText(searchTerm);
          const nomeClean = normalizeText(p.nome_completo);
          const qraClean = normalizeText(p.qra);
          
          const matchText = nomeClean.includes(searchClean) || qraClean.includes(searchClean);
          
          let matchTab = true;
          if (activeTab !== 'todos') {
              matchTab = p.aulas?.some(aula => aula.curso_id === activeTab);
          }

          return matchText && matchTab;
      });
  }, [professores, searchTerm, activeTab]);

  const materiasDoCursoSelecionado = useMemo(() => {
      if (!aulaTemp.curso_id) return [];
      return materias.filter(m => m.curso_id === aulaTemp.curso_id);
  }, [materias, aulaTemp.curso_id]);

  // --- HANDLERS ---

  const handleCpfChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 11) value = value.slice(0, 11);
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    setFormData(prev => ({ ...prev, cpf: value }));
  };

  const handleReChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 7) value = value.slice(0, 7);
    if (value.length > 6) value = value.replace(/^(\d{6})(\d)/, "$1-$2");
    setFormData(prev => ({ ...prev, re: value }));
  };

  const handleTelefoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length > 2) value = value.replace(/^(\d{2})(\d)/, "($1) $2");
    if (value.length > 7) value = value.replace(/(\d{5})(\d)/, "$1-$2");
    setFormData(prev => ({ ...prev, telefone: value }));
  };

  const handleOpenModal = (item = null) => {
    setCurrentItem(item);
    setError('');
    setProcessing(false);
    setFormData(item ? { ...item } : { 
        nome_completo: '', qra: '', posto: '', cpf: '', re: '', 
        telefone: '', email: '', 
        aulas: [] 
    });
    setAulaTemp({ curso_id: '', materia_id: '' });

    if (item && item.posto) {
        if (POSTOS_PERMITIDOS.includes(item.posto)) {
            setPostoSelection(item.posto);
        } else {
            setPostoSelection('Outro'); 
        }
    } else {
        setPostoSelection('');
    }

    setShowModal(true);
  };

  const handleAddAula = () => {
      const { curso_id, materia_id } = aulaTemp;
      if (!curso_id || !materia_id) {
          alert("Selecione o Curso e a Matéria.");
          return;
      }
      const curso = cursos.find(c => c.id === curso_id);
      const materia = materias.find(m => m.id === materia_id);
      if (!curso || !materia) return;

      const novaAula = { 
          curso_id, 
          curso_nome: curso.nome, 
          materia_id, 
          materia_nome: materia.nome 
      };
      
      const existe = formData.aulas?.some(a => a.curso_id === curso_id && a.materia_id === materia_id);
      if (existe) {
          alert("Este professor já está alocado para esta matéria neste curso.");
          return;
      }

      setFormData(prev => ({ ...prev, aulas: [...(prev.aulas || []), novaAula] }));
      setAulaTemp(prev => ({ ...prev, materia_id: '' }));
  };

  const handleRemoveAula = (index) => {
      setFormData(prev => ({
          ...prev,
          aulas: prev.aulas.filter((_, i) => i !== index)
      }));
  };

  const handlePostoSelectionChange = (e) => {
      const val = e.target.value;
      setPostoSelection(val);
      if (val === 'Outro') {
          if (POSTOS_PERMITIDOS.includes(formData.posto)) {
             setFormData(prev => ({ ...prev, posto: '' }));
          }
      } else {
          setFormData(prev => ({ ...prev, posto: val }));
      }
  };

  const handleSubmitProfessor = async (e) => {
      e.preventDefault();
      setProcessing(true);
      setError('');
      try {
          const cpfLimpo = limparCPF(formData.cpf);
          if (cpfLimpo.length !== 11) throw new Error("CPF inválido (deve conter 11 dígitos).");
          
          if (!formData.posto || formData.posto.trim() === "") {
              throw new Error("O campo Posto/Graduação é obrigatório.");
          }

          if (!formData.aulas || formData.aulas.length === 0) {
              throw new Error("Adicione pelo menos um curso/matéria para este professor.");
          }

          const duplicado = professores.find(p => {
              if (currentItem && p.id === currentItem.id) return false;
              const cpfMatch = p.cpf === cpfLimpo;
              const reMatch = p.re && formData.re && p.re === formData.re;
              return cpfMatch || reMatch;
          });

          if (duplicado) {
              if (duplicado.cpf === cpfLimpo) throw new Error(`O CPF informado já pertence a: ${duplicado.nome_completo}`);
              if (duplicado.re === formData.re) throw new Error(`O RE informado já pertence a: ${duplicado.nome_completo}`);
          }

          const payload = {
              nome_completo: formData.nome_completo,
              qra: formData.qra,
              posto: formData.posto, 
              cpf: cpfLimpo,
              re: formData.re,
              telefone: formData.telefone || '', 
              email: formData.email || '',       
              aulas: formData.aulas
          };

          if (currentItem) {
              await updateDoc(doc(db, paths.professores, currentItem.id), payload);
              await logAuditoria(userProfile, "Atualização Professor", { id: currentItem.id, nome: payload.nome_completo });
          } else {
              const qUser = query(collection(db, paths.users), where("cpf", "==", cpfLimpo));
              const snapUser = await getDocs(qUser);
              if (!snapUser.empty) {
                  throw new Error(`Este CPF já possui um usuário cadastrado no sistema.`);
              }

              const batch = writeBatch(db);
              const profRef = doc(collection(db, paths.professores));
              const newId = profRef.id;
              
              batch.set(profRef, { id: newId, ...payload });
              
              const userRef = doc(db, paths.users, newId);
              const userData = {
                  id: newId,
                  nome: formData.nome_completo,
                  cpf: cpfLimpo,
                  senha: cpfLimpo,
                  nivel: 'professor',
                  re: formData.re,
                  email: formData.email || '',
                  telefone: formData.telefone || '',
                  criado_em: new Date().toISOString(),
                  bloqueado: false
              };
              
              batch.set(userRef, userData);
              await batch.commit();
              await logAuditoria(userProfile, "Criação Professor e Usuário", { id: newId, nome: payload.nome_completo });
          }
          setShowModal(false);
      } catch (err) {
          setError(err.message);
      } finally {
          setProcessing(false);
      }
  };

  const handleConfirmDelete = (id) => {
    setDeleteAction(id);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteAction) return;
    setProcessing(true); // Bloqueia botões do modal de confirmação
    try {
        await deleteDoc(doc(db, paths.professores, deleteAction));
        
        try {
            await deleteDoc(doc(db, paths.users, deleteAction));
        } catch (e) {
            console.log("Usuário vinculado não encontrado ou erro ao excluir usuário:", e);
        }

        await logAuditoria(userProfile, "Exclusão Professor", { id: deleteAction });
        setShowConfirm(false);
    } catch (err) { setError(err.message); setShowConfirm(false); }
    finally { setProcessing(false); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <header className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
            <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão APMBB" className="h-12 w-12" />
            <div>
                <div className="flex items-center">
                    <h1 className="text-3xl font-bold text-gray-800">Gestão de Professores</h1>
                </div>
                <p className="text-sm text-gray-600 ml-1">Cadastro, contatos e atribuição de aulas.</p>
            </div>
        </div>
      </header>

      {error && !showModal && (<div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex justify-between"><span>{error}</span><button onClick={() => setError('')}>x</button></div>)}

      {/* --- ÁREA DE PESQUISA E ABAS --- */}
      <div className="mb-6 space-y-4">
          <div className="bg-white p-4 rounded-lg shadow flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1 w-full">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Buscar Professor (Nome/QRA)</label>
                  <input 
                      type="text" 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Digite para pesquisar..." 
                      className="w-full border p-2 rounded text-sm uppercase" 
                  />
              </div>
              <div>
                   <Button onClick={() => handleOpenModal()} variant="success" className="w-full md:w-auto mt-4 md:mt-0 flex items-center gap-2">
                       <PlusIcon /> Novo Professor
                   </Button>
              </div>
          </div>

          <div className="overflow-x-auto pb-2">
              <nav className="flex space-x-2">
                  <button 
                      onClick={() => setActiveTab('todos')} 
                      className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-colors border ${
                          activeTab === 'todos' 
                          ? 'bg-blue-600 text-white border-blue-800 shadow-md' 
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                      Todos os Professores
                  </button>
                  {cursos.map(c => (
                      <button 
                          key={c.id} 
                          onClick={() => setActiveTab(c.id)} 
                          className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-colors border ${
                              activeTab === c.id 
                              ? 'bg-blue-600 text-white border-blue-800 shadow-md' 
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                          {c.nome}
                      </button>
                  ))}
              </nav>
          </div>
      </div>

      {/* --- TABELA DE RESULTADOS --- */}
      <div className="bg-white rounded-lg shadow p-4 animate-fade-in">
          <div className="flex justify-between mb-4 items-center">
              <h3 className="text-xl font-bold text-gray-800">
                  {activeTab === 'todos' ? 'Todos os Professores' : `Professores: ${cursos.find(c => c.id === activeTab)?.nome}`} 
                  <span className="text-sm font-normal text-gray-500 ml-2">({filteredProfessores.length})</span>
              </h3>
          </div>
          
          <div className="overflow-x-auto" style={{ minHeight: '300px' }}>
              <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 uppercase text-gray-500">
                      <tr>
                          <th className="px-4 py-2 text-left">Posto / Nome / QRA</th>
                          <th className="px-4 py-2 text-left">Contatos</th>
                          <th className="px-4 py-2 text-left">Cursos e Matérias</th>
                          <th className="px-4 py-2 text-right">Ações</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                      {filteredProfessores.map(p => (
                          <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-gray-900">
                                  <div className="font-bold">{p.posto} {p.qra}</div>
                                  <div className="text-sm">{p.nome_completo}</div>
                                  <div className="text-xs text-gray-500">RE: {p.re}</div>
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                  {p.telefone && <div className="flex items-center gap-1"><span className="text-xs font-bold text-gray-500">Tel:</span> {p.telefone}</div>}
                                  {p.email && <div className="flex items-center gap-1"><span className="text-xs font-bold text-gray-500">E-mail:</span> {p.email}</div>}
                                  {!p.telefone && !p.email && <span className="text-gray-400 italic text-xs">Sem contato</span>}
                              </td>
                              <td className="px-4 py-3">
                                  <div className="flex flex-col gap-1">
                                      {p.aulas && p.aulas.map((aula, idx) => (
                                          <span key={idx} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                                              activeTab !== 'todos' && aula.curso_id === activeTab 
                                              ? 'bg-blue-100 text-blue-800 border-blue-200 ring-1 ring-blue-300' 
                                              : 'bg-gray-50 text-gray-600 border-gray-200'
                                          }`}>
                                              {aula.curso_nome || aula.turma_nome}: {aula.materia_nome || aula.materia}
                                          </span>
                                      ))}
                                  </div>
                              </td>
                              <td className="px-4 py-3 text-right relative action-menu-container">
                                  {/* Botão Principal com Dropdown */}
                                  <button
                                      onClick={(e) => toggleActionMenu(p.id, e)}
                                      className="text-gray-600 hover:text-blue-600 font-bold text-sm flex items-center justify-end gap-1 ml-auto border border-gray-300 rounded px-3 py-1.5 bg-white hover:bg-gray-50 shadow-sm transition-all"
                                  >
                                      <EditIcon /> Editar
                                  </button>

                                  {/* Menu Dropdown */}
                                  {actionMenuOpenId === p.id && (
                                      <div className="absolute right-0 top-10 w-32 bg-white rounded-md shadow-xl border border-gray-200 z-50 overflow-hidden ring-1 ring-black ring-opacity-5 animate-fade-in-down">
                                          <button
                                              onClick={() => { handleOpenModal(p); setActionMenuOpenId(null); }}
                                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                                          >
                                              <EditIcon /> Editar
                                          </button>
                                          <button
                                              onClick={() => { handleConfirmDelete(p.id); setActionMenuOpenId(null); }}
                                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-gray-100 transition-colors"
                                          >
                                              <TrashIcon /> Excluir
                                          </button>
                                      </div>
                                  )}
                              </td>
                          </tr>
                      ))}
                      {filteredProfessores.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-gray-500">Nenhum professor encontrado.</td></tr>}
                  </tbody>
              </table>
          </div>
      </div>

      {/* Modal Formulário */}
      <Modal show={showModal} onClose={() => setShowModal(false)} title={currentItem ? 'Editar Professor' : 'Novo Professor'} size="2xl">
          <form onSubmit={handleSubmitProfessor} className="space-y-4">
              <div className="bg-gray-50 p-3 rounded border mb-4">
                  <h4 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Dados Pessoais e Contato</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Nome Completo</label>
                          <input value={formData.nome_completo || ''} onChange={e => setFormData({...formData, nome_completo: e.target.value})} className="w-full border p-2 rounded" required />
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">QRA (Nome de Guerra)</label>
                          <input value={formData.qra || ''} onChange={e => setFormData({...formData, qra: e.target.value})} className="w-full border p-2 rounded" required placeholder="Ex: Cap João" />
                      </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Posto/Graduação</label>
                          <select value={postoSelection} onChange={handlePostoSelectionChange} className="w-full border p-2 rounded bg-white" required>
                              <option value="">Selecione...</option>
                              {POSTOS_PERMITIDOS.map(p => (<option key={p} value={p}>{p}</option>))}
                              <option value="Outro">Outro (especificar)</option>
                          </select>
                          {postoSelection === 'Outro' && (<input value={formData.posto || ''} onChange={e => setFormData({...formData, posto: e.target.value})} className="w-full border p-2 rounded mt-2 bg-yellow-50 focus:bg-white" required placeholder="Digite o Posto/Graduação" />)}
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">CPF</label>
                          <input value={formData.cpf || ''} onChange={handleCpfChange} className="w-full border p-2 rounded" required placeholder="000.000.000-00" />
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">RE (7 dígitos)</label>
                          <input value={formData.re || ''} onChange={handleReChange} className="w-full border p-2 rounded" required placeholder="000000-0" />
                      </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                          <label className="block text-sm font-medium text-gray-700">Telefone / Celular</label>
                          <input value={formData.telefone || ''} onChange={handleTelefoneChange} className="w-full border p-2 rounded" placeholder="(11) 99999-9999" />
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-gray-700">E-mail</label>
                          <input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full border p-2 rounded" placeholder="exemplo@policiamilitar.sp.gov.br" />
                      </div>
                  </div>
              </div>
              
              <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-bold text-gray-700 mb-2">Atribuição de Aulas (Cursos e Matérias)</h4>
                  <div className="flex gap-2 mb-2 items-end">
                      <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500">Curso</label>
                          <select 
                            value={aulaTemp.curso_id} 
                            onChange={e => setAulaTemp({...aulaTemp, curso_id: e.target.value, materia_id: ''})} 
                            className="w-full border p-2 rounded text-sm bg-white"
                          >
                              <option value="">Selecione o Curso...</option>
                              {cursos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                          </select>
                      </div>
                      <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500">Matéria Ministrada</label>
                          <select 
                            value={aulaTemp.materia_id} 
                            onChange={e => setAulaTemp({...aulaTemp, materia_id: e.target.value})} 
                            className="w-full border p-2 rounded text-sm bg-white disabled:bg-gray-100"
                            disabled={!aulaTemp.curso_id} 
                          >
                              <option value="">
                                  {!aulaTemp.curso_id ? "Selecione o Curso primeiro" : "Selecione a Matéria..."}
                              </option>
                              {materiasDoCursoSelecionado.map(m => (
                                  <option key={m.id} value={m.id}>{m.nome}</option>
                              ))}
                          </select>
                      </div>
                      <Button onClick={handleAddAula} variant="secondary" type="button" className="h-10 text-sm">Adicionar</Button>
                  </div>
                  
                  <div className="bg-white rounded border p-2 max-h-40 overflow-y-auto">
                      {(!formData.aulas || formData.aulas.length === 0) && <p className="text-xs text-gray-400 text-center">Nenhuma matéria atribuída.</p>}
                      {formData.aulas && formData.aulas.map((aula, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-gray-50 p-2 mb-1 rounded shadow-sm border">
                              <span className="text-sm text-gray-700 font-medium">
                                  {aula.curso_nome || aula.turma_nome}: {aula.materia_nome || aula.materia}
                              </span>
                              <button type="button" onClick={() => handleRemoveAula(idx)} className="text-red-500 hover:text-red-700 font-bold px-2">×</button>
                          </div>
                      ))}
                  </div>
              </div>

              {error && <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm mt-2">{error}</div>}

              {/* RODAPÉ DO MODAL ATUALIZADO */}
              <div className="flex justify-between pt-4 border-t mt-4">
                  {/* Spacer vazio (removido o botão de excluir daqui conforme pedido) */}
                  <div></div>

                  <div className="flex gap-2">
                      <Button type="button" variant="secondary" onClick={() => setShowModal(false)} disabled={processing}>
                          Cancelar
                      </Button>
                      <Button type="submit" variant="primary" disabled={processing}>
                          {processing ? 'Salvando...' : 'Salvar Professor'}
                      </Button>
                  </div>
              </div>
          </form>
      </Modal>

      <Modal show={showConfirm} onClose={() => setShowConfirm(false)} title="Confirmar Exclusão" size="sm">
          <p>Tem certeza que deseja excluir o professor <strong>{professores.find(p => p.id === deleteAction)?.nome_completo}</strong>?</p>
          <div className="flex justify-end gap-2 mt-4"><Button onClick={() => setShowConfirm(false)} variant="secondary">Cancelar</Button><Button onClick={handleDelete} variant="danger">Excluir</Button></div>
      </Modal>
    </div>
  );
};